import React, { useContext, useEffect, useRef, useState } from 'react'
import Table, {
  Sort,
  TableBody,
  TableData,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableSortColumn,
} from 'components/Table'
import { BroadSignGallerySortColumn } from 'types/broad-sign'
import { SortDirection } from 'types/various'
import { useTranslation } from 'react-i18next'
import './BroadSignCreationMediaTable.scss'
import Checkbox from '../../../../components/Form/Checkbox'
import Input from '../../../../components/Form/Input'
import SelectMultiCheckbox from '../../../../components/Form/SelectMultiCheckbox'
import { SelectOption } from '../../../../components/Form/Select'
import Button, { ButtonTheme } from '../../../../components/Form/Button'
import FillingSpinner, { FillingSpinnerType } from '../../../../components/FillingSpinner'
import BroadSignService from '../../services/broad-sign.service'
import { AppContext } from '../../../../contexts/AppContext'
import {
  BROADSIGN_ACCEPTANCE_NOT_SENT,
  BROADSIGN_ACCEPTANCE_SENDING,
  BroadSignAcceptanceStatus,
  BroadSignCreationAcceptanceModel,
} from '../../../../api/broadSign/models/broad-sign-creation-acceptance.model'
import Tooltip from '../../../../components/Tooltip'
import AcceptanceStatusIcon from './AcceptanceStatusIcon'

const EMPTY_VALUE = '-'

// every table opens on its galleries by name, the order a reader looks for one in
const DEFAULT_SORT: Sort = {
  column: BroadSignGallerySortColumn.NAME,
  direction: SortDirection.ASC,
}

// the shared header flips whichever direction it was given, whatever column was clicked. a column
// the table was not ordered by yet starts at the top of its own order instead of inheriting one
const nextSort = (current: Sort, clicked: Sort): Sort =>
  current.column === clicked.column
    ? clicked
    : { column: clicked.column, direction: SortDirection.ASC }

// the tables are ordered on what their cells hold, so the comparison is the reader's own
const sortRows = <T,>(rows: T[], sort: Sort, value: (row: T, column: TableSortColumn) => string) =>
  [...rows].sort((first, second) => {
    const order = value(first, sort.column).localeCompare(value(second, sort.column))

    return sort.direction === SortDirection.ASC ? order : -order
  })

// the status is ordered raw rather than by the word the cell shows: the two statuses that both
// read as sent keep together that way, and the order holds whatever language the page is in
const gallerySortValue = (gallery: GalleryRow, column: TableSortColumn) => {
  switch (column) {
    case BroadSignGallerySortColumn.CITY:
      return gallery.cityName ?? ''
    case BroadSignGallerySortColumn.ACCEPTANCE_STATUS:
      return gallery.acceptanceStatus
    default:
      return gallery.name
  }
}

const acceptanceSortValue = (
  acceptance: BroadSignCreationAcceptanceModel,
  column: TableSortColumn
) => {
  switch (column) {
    case BroadSignGallerySortColumn.CITY:
      return acceptance.city ?? ''
    case BroadSignGallerySortColumn.ACCEPTANCE_STATUS:
      return acceptance.acceptanceStatus
    case BroadSignGallerySortColumn.REJECTION_REASON:
      return acceptance.rejectionDetails ?? ''
    default:
      return acceptance.building
  }
}

// the cities a table has rows in, each of them once and in the order a reader looks for them
const cityOptions = (cities: (string | null)[]): SelectOption[] =>
  Array.from(new Set(cities.filter((city): city is string => !!city)))
    .sort((first, second) => first.localeCompare(second))
    .map(city => ({ value: city, label: city }))

// picking nothing is no filter at all, every row goes through
const matchesPicked = (picked: string[], value: string | null) =>
  !picked.length || (!!value && picked.includes(value))

// the status column tells two states apart and the filter offers the same two, not the raw statuses
// behind them: sending and pending both read as sent and would pick out one and the same set of rows
enum GalleryStatus {
  NOT_SENT = 'not_sent',
  SENT = 'sent',
}

const galleryStatus = (status: string) =>
  isSent(status) ? GalleryStatus.SENT : GalleryStatus.NOT_SENT

const asNeedle = (search: string) => search.trim().toLowerCase()

// the search runs over what the tables show of a gallery, its name and the city it stands in. an
// empty needle is no search at all and every row goes through
const matchesSearch = (needle: string, ...values: (string | null)[]) =>
  !needle || values.some(value => !!value && value.toLowerCase().includes(needle))

// a table shows ten rows and scrolls the rest, the height it stops growing at is in the stylesheet
const SCROLLABLE_ROW_LIMIT = 10

const scrolls = (rowCount: number) => rowCount > SCROLLABLE_ROW_LIMIT

// the answer of the gallery paints its row, the ones still on their way stay plain
const ACCEPTANCE_ROW_CLASS: Record<string, string> = {
  [BroadSignAcceptanceStatus.ACCEPTED]: 'BroadSignCreationMediaTable__response-row--accepted',
  [BroadSignAcceptanceStatus.PENDING]: 'BroadSignCreationMediaTable__response-row--pending',
  [BroadSignAcceptanceStatus.REJECTED]: 'BroadSignCreationMediaTable__response-row--rejected',
}

// the gallery has come back with a decision, an acceptance still on its way has none
const hasDecided = (status: string) =>
  status === BroadSignAcceptanceStatus.ACCEPTED || status === BroadSignAcceptanceStatus.REJECTED

// only an answered acceptance has a decision to show, the rest of the statuses leave the cell empty
const isAnswered = (status: string) =>
  Object.values(BroadSignAcceptanceStatus).includes(status as BroadSignAcceptanceStatus)

const responseRowClassName = (status: string) =>
  ['CommonTable__data-row', ACCEPTANCE_ROW_CLASS[status]].filter(Boolean).join(' ')

// the table a dragged gallery comes from decides what dropping it does, a gallery can only be
// dropped on the other one
enum GalleryTable {
  ACCEPTANCES = 'acceptances',
  FREE = 'free',
}

// a row of the gallery tables, one acceptance of the creation. the id is the acceptance's own, the
// one the assignment flips the flag of
interface GalleryRow {
  id: string
  name: string
  cityName: string | null
  acceptanceStatus: string
}

// the creation has never gone out to the gallery
const isNotSent = (status: string) => status === BROADSIGN_ACCEPTANCE_NOT_SENT

// the creation is on its way to the gallery or already with it, either way the answer is still to
// come and the gallery is out of the hands of the user for as long as that lasts
const isSent = (status: string) =>
  status === BROADSIGN_ACCEPTANCE_SENDING || status === BroadSignAcceptanceStatus.PENDING

// the two statuses above are the whole of the acceptances table, the creation has to be assigned to
// the gallery to reach it at all. an answered gallery is left out on purpose, it is the responses
// table that keeps that one, with the answer it gave
const awaitsAcceptance = (acceptance: BroadSignCreationAcceptanceModel) =>
  acceptance.used && (isNotSent(acceptance.acceptanceStatus) || isSent(acceptance.acceptanceStatus))

// a gallery is free as long as the creation is not assigned to it
const isFree = (acceptance: BroadSignCreationAcceptanceModel) => !acceptance.used

const galleryRows = (
  acceptances: BroadSignCreationAcceptanceModel[],
  matches: (acceptance: BroadSignCreationAcceptanceModel) => boolean
): GalleryRow[] =>
  acceptances.filter(matches).map(acceptance => ({
    id: acceptance.id,
    name: acceptance.building,
    cityName: acceptance.city,
    acceptanceStatus: acceptance.acceptanceStatus,
  }))

const BroadSignCreationMediaTable: React.FC<{
  acceptances: BroadSignCreationAcceptanceModel[]
  creationId: string
  creationName: string
  loading?: boolean
}> = ({ acceptances, creationId, creationName, loading }) => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)

  const [search, setSearch] = useState('')
  const [gallerySort, setGallerySort] = useState<Record<GalleryTable, Sort>>({
    [GalleryTable.ACCEPTANCES]: DEFAULT_SORT,
    [GalleryTable.FREE]: DEFAULT_SORT,
  })
  const [responsesSort, setResponsesSort] = useState<Sort>(DEFAULT_SORT)
  const [galleryCities, setGalleryCities] = useState<Record<GalleryTable, string[]>>({
    [GalleryTable.ACCEPTANCES]: [],
    [GalleryTable.FREE]: [],
  })
  const [responsesCities, setResponsesCities] = useState<string[]>([])
  const [acceptanceStatuses, setAcceptanceStatuses] = useState<string[]>([])
  const [responsesDecisions, setResponsesDecisions] = useState<string[]>([])
  const [cities, setCities] = useState<string[]>([])
  // the field above the tables narrows all three, the field of a table narrows that one alone
  const [gallerySearch, setGallerySearch] = useState<Record<GalleryTable, string>>({
    [GalleryTable.ACCEPTANCES]: '',
    [GalleryTable.FREE]: '',
  })
  const [responsesSearch, setResponsesSearch] = useState('')
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [selectedFreeKeys, setSelectedFreeKeys] = useState<string[]>([])
  const [draggedFrom, setDraggedFrom] = useState<GalleryTable | null>(null)
  const [dropTarget, setDropTarget] = useState<GalleryTable | null>(null)
  const [loadingTable, setLoadingTable] = useState<GalleryTable | null>(null)
  // the tooltip follows the cursor instead of the cell, see the fixed positioning it gets
  const [hoveredDecision, setHoveredDecision] = useState<{
    id: string
    x: number
    y: number
  } | null>(null)

  const needle = asNeedle(search)

  // the whole of the acceptances table, the button below it sends the creation to all of them and
  // has no business narrowing with the search
  const allGalleries = galleryRows(acceptances, awaitsAcceptance)

  const allFreeGalleries = galleryRows(acceptances, isFree)

  // the responses table lists the decisions, the acceptances still waiting on one are only counted
  const allDecidedAcceptances = acceptances.filter(acceptance =>
    hasDecided(acceptance.acceptanceStatus)
  )

  // the cities on offer are the ones the whole table stands in, so that picking one does not take
  // the rest of them out of the list it was picked from
  const cityFilterOptions = {
    [GalleryTable.ACCEPTANCES]: cityOptions(allGalleries.map(gallery => gallery.cityName)),
    [GalleryTable.FREE]: cityOptions(allFreeGalleries.map(gallery => gallery.cityName)),
  }

  const responsesCityOptions = cityOptions(allDecidedAcceptances.map(acceptance => acceptance.city))

  const allCityOptions = cityOptions([
    ...allGalleries.map(gallery => gallery.cityName),
    ...allFreeGalleries.map(gallery => gallery.cityName),
    ...allDecidedAcceptances.map(acceptance => acceptance.city),
  ])

  const statusFilterOptions: SelectOption[] = [
    { value: GalleryStatus.NOT_SENT, label: t('broadSign.not_sent') },
    { value: GalleryStatus.SENT, label: t('broadSign.sent') },
  ]

  const decisionFilterOptions: SelectOption[] = [
    { value: BroadSignAcceptanceStatus.ACCEPTED, label: t('broadSign.accepted') },
    { value: BroadSignAcceptanceStatus.REJECTED, label: t('broadSign.rejected') },
  ]

  const galleries = sortRows(
    allGalleries
      .filter(gallery => matchesSearch(needle, gallery.name, gallery.cityName))
      .filter(gallery =>
        matchesSearch(
          asNeedle(gallerySearch[GalleryTable.ACCEPTANCES]),
          gallery.name,
          gallery.cityName
        )
      )
      .filter(gallery => matchesPicked(cities, gallery.cityName))
      .filter(gallery => matchesPicked(galleryCities[GalleryTable.ACCEPTANCES], gallery.cityName))
      .filter(gallery =>
        matchesPicked(acceptanceStatuses, galleryStatus(gallery.acceptanceStatus))
      ),
    gallerySort[GalleryTable.ACCEPTANCES],
    gallerySortValue
  )

  const freeGalleries = sortRows(
    allFreeGalleries
      .filter(gallery => matchesSearch(needle, gallery.name, gallery.cityName))
      .filter(gallery =>
        matchesSearch(asNeedle(gallerySearch[GalleryTable.FREE]), gallery.name, gallery.cityName)
      )
      .filter(gallery => matchesPicked(cities, gallery.cityName))
      .filter(gallery => matchesPicked(galleryCities[GalleryTable.FREE], gallery.cityName)),
    gallerySort[GalleryTable.FREE],
    gallerySortValue
  )

  const decidedAcceptances = sortRows(
    allDecidedAcceptances
      .filter(acceptance => matchesSearch(needle, acceptance.building, acceptance.city))
      .filter(acceptance =>
        matchesSearch(asNeedle(responsesSearch), acceptance.building, acceptance.city)
      )
      .filter(acceptance => matchesPicked(cities, acceptance.city))
      .filter(acceptance => matchesPicked(responsesCities, acceptance.city))
      .filter(acceptance => matchesPicked(responsesDecisions, acceptance.acceptanceStatus)),
    responsesSort,
    acceptanceSortValue
  )

  const draggedGalleries = useRef<GalleryRow[]>([])

  useEffect(() => {
    draggedGalleries.current = []
    setSelectedKeys([])
    setSelectedFreeKeys([])
    setDraggedFrom(null)
    setDropTarget(null)
    setLoadingTable(null)
  }, [acceptances])

  const onGallerySort = (table: GalleryTable) => (clicked: Sort) =>
    setGallerySort(current => ({ ...current, [table]: nextSort(current[table], clicked) }))

  // the search and the city filter take rows out from under a selection, so both start a new one
  useEffect(() => {
    setSelectedKeys([])
    setSelectedFreeKeys([])
  }, [search, gallerySearch, cities, galleryCities, acceptanceStatuses])

  // both tables keep their own selection, the table a row belongs to decides which one it lands in
  const rows = (table: GalleryTable) =>
    table === GalleryTable.ACCEPTANCES ? galleries : freeGalleries

  const selection = (table: GalleryTable) =>
    table === GalleryTable.ACCEPTANCES ? selectedKeys : selectedFreeKeys

  const setSelection = (table: GalleryTable) =>
    table === GalleryTable.ACCEPTANCES ? setSelectedKeys : setSelectedFreeKeys

  // a gallery the creation is already out to is frozen where it stands, it can be neither picked
  // nor dragged back. only the acceptances table ever holds one, a free gallery has nothing out
  const isLocked = (table: GalleryTable, gallery: GalleryRow) =>
    table === GalleryTable.ACCEPTANCES && isSent(gallery.acceptanceStatus)

  const selectableRows = (table: GalleryTable) =>
    rows(table).filter(gallery => !isLocked(table, gallery))

  const isSelected = (table: GalleryTable, key: string) => selection(table).includes(key)

  const allSelected = (table: GalleryTable) =>
    !!selectableRows(table).length && selection(table).length === selectableRows(table).length

  const someSelected = (table: GalleryTable) => !!selection(table).length && !allSelected(table)

  const toggleGallery = (table: GalleryTable, key: string) =>
    setSelection(table)(keys =>
      keys.includes(key) ? keys.filter(selected => selected !== key) : [...keys, key]
    )

  const toggleAllGalleries = (table: GalleryTable) =>
    setSelection(table)(allSelected(table) ? [] : selectableRows(table).map(gallery => gallery.id))

  // the whole creation goes to acceptance, the galleries it covers are the ones already assigned
  const onSendToAcceptances = () =>
    void BroadSignService.sendCreationToAcceptance(creationId, addNotification, t)

  // the flag of the dropped galleries is what moves them between the tables, and the mutation only
  // touches the galleries it is handed, so the ones left alone keep the flag they already carry
  const assignMedia = (dropped: GalleryRow[], used: boolean) => {
    if (!dropped.length) {
      return
    }

    const buildings = dropped.map(gallery => ({ id: gallery.id, used }))

    void BroadSignService.assignCreationMedia(creationId, buildings, addNotification, t)
  }

  // dragging a selected row takes the whole selection of its table along, dragging an unselected
  // one is a drag of that row alone and leaves the selection where it is
  const draggedRows = (gallery: GalleryRow, from: GalleryTable) =>
    isSelected(from, gallery.id) ? rows(from).filter(row => isSelected(from, row.id)) : [gallery]

  // the ghost stays the row the drag started on, a copy of it out of the table it belongs to,
  // with the count of the rows travelling along pinned to its corner. the browser snapshots the
  // drag image on dragstart, so the copy has to be in the document by then and is good for that
  // one snapshot only
  const setDragCount = (event: React.DragEvent<HTMLTableRowElement>, count: number) => {
    const row = event.currentTarget
    const rect = row.getBoundingClientRect()

    const image = document.createElement('div')
    image.className = 'BroadSignCreationMediaTable__drag-image'
    image.style.width = `${rect.width}px`

    // the row keeps the frame and the column widths its own table gave it
    const container = document.createElement('div')
    container.className =
      row.closest('.BroadSignCreationMediaTable__table')?.className ??
      'BroadSignCreationMediaTable__table'

    const table = document.createElement('table')
    table.className = 'CommonTable'

    const body = document.createElement('tbody')
    const clone = row.cloneNode(true) as HTMLTableRowElement

    Array.from(row.cells).forEach((cell, index) => {
      clone.cells[index].style.width = `${cell.getBoundingClientRect().width}px`
    })

    const badge = document.createElement('div')
    badge.className = 'BroadSignCreationMediaTable__drag-badge'
    badge.textContent = String(count)

    body.appendChild(clone)
    table.appendChild(body)
    container.appendChild(table)
    image.append(container, badge)
    document.body.appendChild(image)

    // the ghost hangs off the cursor the way the plain row one does
    event.dataTransfer.setDragImage(image, event.clientX - rect.left, event.clientY - rect.top)

    window.setTimeout(() => image.remove())
  }

  const onDragStart =
    (gallery: GalleryRow, from: GalleryTable) => (event: React.DragEvent<HTMLTableRowElement>) => {
      const dragged = draggedRows(gallery, from)

      draggedGalleries.current = dragged
      setDraggedFrom(from)
      // firefox starts no drag at all without a payload on the transfer
      event.dataTransfer.setData('text/plain', dragged.map(row => row.id).join(','))
      event.dataTransfer.effectAllowed = 'move'

      // a single row drags as itself, a selection drags as the grabbed row plus its count
      if (dragged.length > 1) {
        setDragCount(event, dragged.length)
      }
    }

  const onDragEnd = () => {
    draggedGalleries.current = []
    setDraggedFrom(null)
    setDropTarget(null)
  }

  // the rows on the move, greyed out in the table they are being dragged out of
  const isDragged = (table: GalleryTable, key: string) =>
    draggedFrom === table && draggedGalleries.current.some(row => row.id === key)

  // only the table the gallery does not come from takes the drop
  const takesDrop = (table: GalleryTable) => !!draggedFrom && draggedFrom !== table

  const onDragOver = (table: GalleryTable) => (event: React.DragEvent<HTMLDivElement>) => {
    if (!takesDrop(table)) {
      return
    }

    // the default handler refuses the drop, preventing it is what marks the zone as droppable
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDropTarget(table)
  }

  // dragging over a row leaves the wrapper only when the pointer lands outside of it
  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return
    }

    setDropTarget(null)
  }

  const onDrop = (table: GalleryTable) => (event: React.DragEvent<HTMLDivElement>) => {
    const dragged = draggedGalleries.current

    if (!takesDrop(table) || !dragged.length) {
      return
    }

    event.preventDefault()
    assignMedia(dragged, table === GalleryTable.ACCEPTANCES)
    // the rows are on their way into the table they were dropped on, that is where the wait is shown
    setLoadingTable(table)
    onDragEnd()
  }

  // a reload no drop asked for (a different creation was picked) has no destination to point at
  const spinnerTable = loadingTable ?? GalleryTable.ACCEPTANCES

  const spinner = (table: GalleryTable) =>
    loading && spinnerTable === table ? (
      <FillingSpinner
        type={FillingSpinnerType.STANDARD}
        className='BroadSignCreationMediaTable__spinner'
      />
    ) : null

  const tableClassName = (table: GalleryTable) =>
    [
      'BroadSignCreationMediaTable__table',
      table === GalleryTable.FREE && 'BroadSignCreationMediaTable__table--free',
      takesDrop(table) && 'BroadSignCreationMediaTable__table--droppable',
      dropTarget === table && 'BroadSignCreationMediaTable__table--drop-over',
      scrolls(rows(table).length) && 'BroadSignCreationMediaTable__table--scrollable',
    ]
      .filter(Boolean)
      .join(' ')

  // the responses table takes no drop, the row count is the only thing that modifies it
  const responsesTableClassName = [
    'BroadSignCreationMediaTable__table',
    'BroadSignCreationMediaTable__table--responses',
    scrolls(decidedAcceptances.length) && 'BroadSignCreationMediaTable__table--scrollable',
  ]
    .filter(Boolean)
    .join(' ')

  // the filters of a table ride in its title bar, out of the way of the columns they narrow. the
  // menus are portalled, the box the table scrolls in would otherwise cut them off
  const filterSelect = (
    id: string,
    options: SelectOption[],
    picked: string[],
    onPick: (values: string[]) => void,
    allLabel: string
  ) => (
    <SelectMultiCheckbox
      id={id}
      value={picked}
      options={options}
      onChange={onPick}
      selectAllOption
      allLabel={allLabel}
      menuPortalTarget={document.body}
      dataCy={id}
    />
  )

  const tableSearchInput = (id: string, value: string, onChange: (search: string) => void) => (
    <div className='BroadSignCreationMediaTable__title-search'>
      <Input
        id={id}
        value={value}
        onChange={onChange}
        placeholder={t('common.search')}
        dataCy={id}
      />
    </div>
  )

  const onGallerySearch = (table: GalleryTable) => (value: string) =>
    setGallerySearch(current => ({ ...current, [table]: value }))

  const cityFilter = (
    id: string,
    options: SelectOption[],
    picked: string[],
    onPick: (cities: string[]) => void
  ) => filterSelect(id, options, picked, onPick, t('broadSign.cities'))

  const onPickGalleryCities = (table: GalleryTable) => (cities: string[]) =>
    setGalleryCities(current => ({ ...current, [table]: cities }))

  const selectAllHeader = (table: GalleryTable) => (
    <TableHeaderItem className='CommonTable__header-item'>
      <Checkbox
        id={`broadsign-${table}-select-all`}
        checked={allSelected(table)}
        indeterminate={someSelected(table)}
        onChange={() => toggleAllGalleries(table)}
        disabled={loading || !selectableRows(table).length}
        dataCy={`broadsign-${table}-select-all`}
      />
    </TableHeaderItem>
  )

  const rowHandle = (table: GalleryTable, gallery: GalleryRow) => (
    <TableData className='CommonTable__data-item'>
      {/* the checkbox toggles on its own, the row click must not toggle it back */}
      <div onClick={event => event.stopPropagation()}>
        <Checkbox
          id={`broadsign-${table}-${gallery.id}`}
          checked={isSelected(table, gallery.id)}
          onChange={() => toggleGallery(table, gallery.id)}
          disabled={loading || isLocked(table, gallery)}
        />
      </div>
    </TableData>
  )

  const rowClassName = (table: GalleryTable, gallery: GalleryRow) =>
    [
      'CommonTable__data-row',
      'BroadSignCreationMediaTable__row',
      isDragged(table, gallery.id) && 'BroadSignCreationMediaTable__row--dragged',
      isLocked(table, gallery) && 'BroadSignCreationMediaTable__row--locked',
    ]
      .filter(Boolean)
      .join(' ')

  // the galleries still to be sent, the frozen ones are already on the other side of the button
  const sendableGalleries = allGalleries.filter(gallery => !isSent(gallery.acceptanceStatus))

  const acceptanceCount = (status: string) =>
    acceptances.filter(acceptance => acceptance.acceptanceStatus === status).length

  const summaryStat = (labelKey: string, count: number) => (
    <div className='BroadSignCreationMediaTable__summary-stat'>
      <span className='BroadSignCreationMediaTable__summary-label'>{t(labelKey)}</span>
      <span className='BroadSignCreationMediaTable__summary-value'>{count}</span>
    </div>
  )

  const noData = (
    <div className='BroadSignCreationMediaTable__no-data'>
      <p>{t('common.noData')}</p>
    </div>
  )

  return (
    <div className='BroadSignCreationMediaTable'>
      <div className='BroadSignCreationMediaTable__search'>
        <div className='BroadSignCreationMediaTable__search-input'>
          <Input
            id='broadsign-gallery-search'
            title={t('common.search')}
            value={search}
            onChange={value => setSearch(value)}
            placeholder={t('broadSign.gallerySearchPlaceholder')}
            dataCy='broadsign-gallery-search'
          />
        </div>

        {cityFilter('broadsign-city-filter', allCityOptions, cities, setCities)}
      </div>

      <div
        className={tableClassName(GalleryTable.ACCEPTANCES)}
        onDragOver={onDragOver(GalleryTable.ACCEPTANCES)}
        onDragLeave={onDragLeave}
        onDrop={onDrop(GalleryTable.ACCEPTANCES)}
        data-cy='broadsign-acceptances-drop-zone'
      >
        <div className='BroadSignCreationMediaTable__title'>
          <span>{t('broadSign.acceptances')}</span>

          <div className='BroadSignCreationMediaTable__title-filters'>
            {tableSearchInput(
              'broadsign-acceptances-search',
              gallerySearch[GalleryTable.ACCEPTANCES],
              onGallerySearch(GalleryTable.ACCEPTANCES)
            )}

            {cityFilter(
              'broadsign-acceptances-city-filter',
              cityFilterOptions[GalleryTable.ACCEPTANCES],
              galleryCities[GalleryTable.ACCEPTANCES],
              onPickGalleryCities(GalleryTable.ACCEPTANCES)
            )}

            {filterSelect(
              'broadsign-acceptances-status-filter',
              statusFilterOptions,
              acceptanceStatuses,
              setAcceptanceStatuses,
              t('broadSign.acceptanceStatus')
            )}
          </div>

          {spinner(GalleryTable.ACCEPTANCES)}
        </div>

        {galleries.length ? (
          <Table
            className='CommonTable'
            sort={gallerySort[GalleryTable.ACCEPTANCES]}
            onSort={onGallerySort(GalleryTable.ACCEPTANCES)}
          >
            <TableHeader sticky>
              <>
                {selectAllHeader(GalleryTable.ACCEPTANCES)}
                <TableHeaderItem
                  className='CommonTable__header-item'
                  sortBy={BroadSignGallerySortColumn.NAME}
                >
                  {t<string>('broadSign.name')}
                </TableHeaderItem>
                <TableHeaderItem
                  className='CommonTable__header-item'
                  sortBy={BroadSignGallerySortColumn.CITY}
                >
                  {t<string>('broadSign.city')}
                </TableHeaderItem>
                <TableHeaderItem
                  className='CommonTable__header-item'
                  sortBy={BroadSignGallerySortColumn.ACCEPTANCE_STATUS}
                >
                  {t<string>('broadSign.acceptanceStatus')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.creationName')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.screenNumber')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.category')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.competitors')}
                </TableHeaderItem>
              </>
            </TableHeader>

            <TableBody>
              {galleries.map(gallery => (
                <TableRow
                  key={gallery.id}
                  className={rowClassName(GalleryTable.ACCEPTANCES, gallery)}
                  draggable={!loading && !isLocked(GalleryTable.ACCEPTANCES, gallery)}
                  onDragStart={onDragStart(gallery, GalleryTable.ACCEPTANCES)}
                  onDragEnd={onDragEnd}
                  onClick={
                    isLocked(GalleryTable.ACCEPTANCES, gallery)
                      ? undefined
                      : () => toggleGallery(GalleryTable.ACCEPTANCES, gallery.id)
                  }
                >
                  <>
                    {rowHandle(GalleryTable.ACCEPTANCES, gallery)}
                    <TableData className='CommonTable__data-item'>
                      <span>{gallery.name}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{gallery.cityName || EMPTY_VALUE}</span>
                    </TableData>
                    {/* a gallery still to be sent has no status to tell, the cell stays empty */}
                    <TableData className='CommonTable__data-item'>
                      <span>{isSent(gallery.acceptanceStatus) ? t('broadSign.sent') : ''}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{creationName}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                  </>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          noData
        )}
      </div>

      <div className='BroadSignCreationMediaTable__actions'>
        <Button
          theme={ButtonTheme.GREEN}
          onClick={onSendToAcceptances}
          disabled={!sendableGalleries.length}
          dataCy='broadsign-send-to-acceptances'
        >
          {t('broadSign.sendToAcceptances')}
        </Button>
      </div>

      <div
        className={tableClassName(GalleryTable.FREE)}
        onDragOver={onDragOver(GalleryTable.FREE)}
        onDragLeave={onDragLeave}
        onDrop={onDrop(GalleryTable.FREE)}
        data-cy='broadsign-free-media-drop-zone'
      >
        <div className='BroadSignCreationMediaTable__title BroadSignCreationMediaTable__title--free'>
          <span>{t('broadSign.competitors')}</span>

          <div className='BroadSignCreationMediaTable__title-filters'>
            {tableSearchInput(
              'broadsign-free-search',
              gallerySearch[GalleryTable.FREE],
              onGallerySearch(GalleryTable.FREE)
            )}

            {cityFilter(
              'broadsign-free-city-filter',
              cityFilterOptions[GalleryTable.FREE],
              galleryCities[GalleryTable.FREE],
              onPickGalleryCities(GalleryTable.FREE)
            )}
          </div>

          {spinner(GalleryTable.FREE)}
        </div>

        {freeGalleries.length ? (
          <Table
            className='CommonTable'
            sort={gallerySort[GalleryTable.FREE]}
            onSort={onGallerySort(GalleryTable.FREE)}
          >
            <TableHeader sticky>
              <>
                {selectAllHeader(GalleryTable.FREE)}
                <TableHeaderItem
                  className='CommonTable__header-item'
                  sortBy={BroadSignGallerySortColumn.NAME}
                >
                  {t<string>('broadSign.name')}
                </TableHeaderItem>
                <TableHeaderItem
                  className='CommonTable__header-item'
                  sortBy={BroadSignGallerySortColumn.CITY}
                >
                  {t<string>('broadSign.city')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.creationName')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.category')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.competitors')}
                </TableHeaderItem>
              </>
            </TableHeader>

            <TableBody>
              {freeGalleries.map(gallery => (
                <TableRow
                  key={gallery.id}
                  className={rowClassName(GalleryTable.FREE, gallery)}
                  draggable={!loading}
                  onDragStart={onDragStart(gallery, GalleryTable.FREE)}
                  onDragEnd={onDragEnd}
                  onClick={() => toggleGallery(GalleryTable.FREE, gallery.id)}
                >
                  <>
                    {rowHandle(GalleryTable.FREE, gallery)}
                    <TableData className='CommonTable__data-item'>
                      <span>{gallery.name}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{gallery.cityName || EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{creationName}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                  </>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          noData
        )}
      </div>

      <div className={responsesTableClassName}>
        <div className='BroadSignCreationMediaTable__title BroadSignCreationMediaTable__title--responses'>
          <span>{t('broadSign.galleryResponses')}</span>

          <div className='BroadSignCreationMediaTable__title-filters'>
            {tableSearchInput('broadsign-responses-search', responsesSearch, setResponsesSearch)}

            {cityFilter(
              'broadsign-responses-city-filter',
              responsesCityOptions,
              responsesCities,
              setResponsesCities
            )}

            {filterSelect(
              'broadsign-responses-decision-filter',
              decisionFilterOptions,
              responsesDecisions,
              setResponsesDecisions,
              t('broadSign.decision')
            )}
          </div>
        </div>

        {decidedAcceptances.length ? (
          <Table
            className='CommonTable'
            sort={responsesSort}
            onSort={clicked => setResponsesSort(current => nextSort(current, clicked))}
          >
            <TableHeader sticky>
              <>
                <TableHeaderItem
                  className='CommonTable__header-item'
                  sortBy={BroadSignGallerySortColumn.NAME}
                >
                  {t<string>('broadSign.galleryName')}
                </TableHeaderItem>
                <TableHeaderItem
                  className='CommonTable__header-item'
                  sortBy={BroadSignGallerySortColumn.CITY}
                >
                  {t<string>('broadSign.city')}
                </TableHeaderItem>
                <TableHeaderItem
                  className='CommonTable__header-item'
                  sortBy={BroadSignGallerySortColumn.ACCEPTANCE_STATUS}
                >
                  {t<string>('broadSign.decision')}
                </TableHeaderItem>
                <TableHeaderItem
                  className='CommonTable__header-item'
                  sortBy={BroadSignGallerySortColumn.REJECTION_REASON}
                >
                  {t<string>('broadSign.rejectionReason')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.category')}
                </TableHeaderItem>
                <TableHeaderItem className='CommonTable__header-item'>
                  {t<string>('broadSign.competitors')}
                </TableHeaderItem>
              </>
            </TableHeader>

            <TableBody>
              {decidedAcceptances.map(acceptance => (
                <TableRow
                  key={acceptance.id}
                  className={responseRowClassName(acceptance.acceptanceStatus)}
                >
                  <>
                    <TableData className='CommonTable__data-item'>
                      <span>{acceptance.building || EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{acceptance.city || EMPTY_VALUE}</span>
                    </TableData>
                    <TableData
                      className='CommonTable__data-item'
                      onMouseEnter={event => {
                        if (isAnswered(acceptance.acceptanceStatus)) {
                          setHoveredDecision({
                            id: acceptance.id,
                            x: event.clientX,
                            y: event.clientY,
                          })
                        }
                      }}
                      onMouseLeave={() => setHoveredDecision(null)}
                    >
                      <div className='BroadSignCreationMediaTable__decision'>
                        <AcceptanceStatusIcon status={acceptance.acceptanceStatus} />
                      </div>

                      {hoveredDecision?.id === acceptance.id && (
                        <div
                          className='BroadSignCreationMediaTable__decision-tooltip'
                          style={{ top: hoveredDecision.y, left: hoveredDecision.x }}
                        >
                          <Tooltip>{t(`broadSign.${acceptance.acceptanceStatus}`)}</Tooltip>
                        </div>
                      )}
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{acceptance.rejectionDetails || EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                    <TableData className='CommonTable__data-item'>
                      <span>{EMPTY_VALUE}</span>
                    </TableData>
                  </>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          noData
        )}
      </div>

      <div className='BroadSignCreationMediaTable__summary'>
        <div className='BroadSignCreationMediaTable__summary-stats'>
          {summaryStat(
            'broadSign.acceptedGalleries',
            acceptanceCount(BroadSignAcceptanceStatus.ACCEPTED)
          )}
          {summaryStat('broadSign.pendingGalleries', acceptanceCount(BROADSIGN_ACCEPTANCE_SENDING))}
          {summaryStat(
            'broadSign.rejectedGalleries',
            acceptanceCount(BroadSignAcceptanceStatus.REJECTED)
          )}
        </div>
      </div>
    </div>
  )
}

export default BroadSignCreationMediaTable
